import React, { Component } from 'react'
import Context from '../models/Context'
import { ReactComponent as CrossIcon } from '../assets/cross.svg'
import { isStandalone, isIos } from '../lib/utils/pwa'

const DISMISS_KEY = 'pwa-install-dismissed'

// In-app install affordance. On Chromium it captures the `beforeinstallprompt`
// event and offers a custom Install button (calling the deferred prompt). On
// iOS Safari — where `beforeinstallprompt` never fires — it shows an
// "Add to Home Screen" hint instead. It stays hidden when already installed
// (standalone) or once the user has dismissed it.
class InstallPrompt extends Component {
  constructor (props) {
    super(props)

    this.state = {
      deferredPrompt: null, // Chromium BeforeInstallPromptEvent
      visible: false,
      iosHint: false
    }

    this.onBeforeInstallPrompt = this.onBeforeInstallPrompt.bind(this)
    this.onAppInstalled = this.onAppInstalled.bind(this)
    this.onInstallClick = this.onInstallClick.bind(this)
    this.onDismiss = this.onDismiss.bind(this)
  }

  componentDidMount () {
    if (isStandalone() || this.isDismissed()) { return }

    window.addEventListener('beforeinstallprompt', this.onBeforeInstallPrompt)
    window.addEventListener('appinstalled', this.onAppInstalled)

    // iOS never fires `beforeinstallprompt`; show the manual hint there.
    if (isIos()) {
      this.setState({ visible: true, iosHint: true })
    }
  }

  componentWillUnmount () {
    window.removeEventListener('beforeinstallprompt', this.onBeforeInstallPrompt)
    window.removeEventListener('appinstalled', this.onAppInstalled)
  }

  isDismissed () {
    try {
      return window.localStorage.getItem(DISMISS_KEY) === '1'
    } catch (e) {
      return false
    }
  }

  rememberDismissed () {
    try {
      window.localStorage.setItem(DISMISS_KEY, '1')
    } catch (e) { }
  }

  onBeforeInstallPrompt (e) {
    // Prevent the mini-infobar; show our own affordance instead.
    e.preventDefault()
    this.setState({ deferredPrompt: e, visible: true, iosHint: false })
  }

  onAppInstalled () {
    this.rememberDismissed()
    this.setState({ deferredPrompt: null, visible: false })
  }

  async onInstallClick () {
    const { deferredPrompt } = this.state
    if (!deferredPrompt) { return }

    deferredPrompt.prompt()
    try {
      await deferredPrompt.userChoice
    } catch (e) { }
    // The event can only be used once; hide the banner regardless of outcome.
    this.setState({ deferredPrompt: null, visible: false })
  }

  onDismiss () {
    this.rememberDismissed()
    this.setState({ visible: false })
  }

  render () {
    if (!this.state.visible) { return null }

    return (
      <Context.Consumer>
        {(context) => {
          const { t } = context
          const { iosHint } = this.state

          return (
            <div className="fixed bottom-0 inset-x-0 z-50 p-4 flex justify-center pointer-events-none">
              <div className="pointer-events-auto bg-white shadow-md border border-gray-lightest rounded-md p-4 max-w-md w-full flex items-start space-x-3">
                <div className="flex-1">
                  <p className="font-bold">{t('PWA_INSTALL.TITLE')}</p>
                  <p className="text-sm text-gray-lighter py-1">
                    {iosHint ? t('PWA_INSTALL.IOS_HINT') : t('PWA_INSTALL.TEXT')}
                  </p>
                  {!iosHint &&
                    <button
                      onClick={this.onInstallClick}
                      className="btn-primary mt-2"
                      type="button"
                    >
                      {t('PWA_INSTALL.BUTTON_INSTALL')}
                    </button>}
                </div>
                <button
                  onClick={this.onDismiss}
                  className="btn-icon-small text-gray-lighter"
                  type="button"
                  aria-label={t('PWA_INSTALL.BUTTON_DISMISS')}
                >
                  <CrossIcon />
                </button>
              </div>
            </div>
          )
        }}
      </Context.Consumer>
    )
  }
}

export default InstallPrompt
